import isEmpty from "lodash/isEmpty";
import { createSelector } from "reselect";
import { Util } from "@grandvisionhq/forms";
import { getShipping, isConsentGiven, getConsents } from "../../selectors.js";
import { ShippingMethod } from "../../models/step-shipping/models/step-shipping/shipping-methods.js";
const { getValuesFromState } = Util;
const getSelectedShippingMethod = createSelector(
  getShipping,
  (shipping) => shipping && shipping.selectedShippingMethod
);
const getSelectedStore = createSelector(
  getShipping,
  (shipping) => shipping && shipping.selectedStore ? shipping.selectedStore : void 0
);
const getSelectedPickupPoint = createSelector(
  getShipping,
  (shipping) => shipping && shipping.selectedPickupPoint ? shipping.selectedPickupPoint : void 0
);
const shouldValidateMyAccount = createSelector(
  getShipping,
  (shipping) => shipping && shipping.shouldValidateMyAccount ? shipping.shouldValidateMyAccount : false
);
const shouldValidateServiceStore = createSelector(
  getShipping,
  (shipping) => shipping && shipping.shouldValidateServiceStore ? shipping.shouldValidateServiceStore : false
);
const getShippingDetails = createSelector(
  getShipping,
  (shipping) => shipping && shipping.shippingDetails
);
const dataOrUndefined = (form) => form && form.isEnabled ? getValuesFromState(form.formData) : void 0;
createSelector(
  getShippingDetails,
  (details) => Boolean(details?.contactDetails)
);
createSelector(
  getShippingDetails,
  (details) => details && dataOrUndefined(details.contactDetails)
);
createSelector(
  getShippingDetails,
  (details) => details && dataOrUndefined(details.expressCheckout)
);
const isBillingAndShippingAddressSame = createSelector(
  getShipping,
  (shipping) => shipping && shipping.isBillingAndDeliveryAddressSame || false
);
const isDeliveryFormEnabled = createSelector(
  getShippingDetails,
  (details) => details && details.deliveryAddress && details.deliveryAddress.isEnabled
);
const getExpressCheckoutFormData = createSelector(
  getShippingDetails,
  (details) => details && details.expressCheckout.formData
);
const getCompanyFormData = createSelector(
  getShippingDetails,
  (details) => details && details.company.formData
);
const getInvoiceFormData = createSelector(
  getShippingDetails,
  (details) => details && details.invoice.formData
);
const getInvoiceFormEnabledState = createSelector(
  getShippingDetails,
  (details) => details && details.invoice.isEnabled
);
const getContactDetailsFormData = createSelector(
  getShippingDetails,
  (details) => details && details.contactDetails.formData
);
const getBillingAddressFormData = createSelector(
  getShippingDetails,
  (details) => details && details.billingAddress.formData
);
const getDeliveryAddressFormData = createSelector(
  getShippingDetails,
  (details) => details && details.deliveryAddress.formData
);
const getBillingAddress = createSelector(
  getShippingDetails,
  (details) => details && dataOrUndefined(details.billingAddress)
);
createSelector(
  getShippingDetails,
  (details) => Boolean(details?.billingAddress)
);
createSelector(
  getShippingDetails,
  (details) => details && details.deliveryAddress && !isEmpty(details.deliveryAddress.formData)
);
const getDeliveryAddress = createSelector(
  getShippingDetails,
  (details) => details && dataOrUndefined(details.deliveryAddress)
);
const hasStoredDeliveryAddress = createSelector(
  getShippingDetails,
  (details) => {
    const values = details && getValuesFromState(details?.deliveryAddress?.formData);
    return !values ? false : Object.keys(values).some((key) => values[key]);
  }
);
const getCompany = createSelector(
  getShippingDetails,
  (details) => details && dataOrUndefined(details.company)
);
createSelector(
  getShippingDetails,
  (details) => details && dataOrUndefined(details.invoice)
);
const getShippingData = createSelector(
  getShippingDetails,
  (details) => details && Object.entries(details).reduce((filtered, [name, form]) => {
    if (form.isEnabled) {
      filtered[name] = getValuesFromState(form.formData);
    }
    return filtered;
  }, {})
);
const isShippingStepValid = createSelector(
  getSelectedShippingMethod,
  getSelectedStore,
  getSelectedPickupPoint,
  getShippingDetails,
  isConsentGiven,
  getConsents,
  (shippingMethod, store, pickupPoint, details, consentGiven, consents) => {
    return !!details && Object.entries(details).filter(([key]) => key !== "expressCheckout").filter(([, form]) => form.isEnabled).every(([, form]) => form.isValid) && (shippingMethod?.method === ShippingMethod.Pickup ? store !== void 0 : true) && (shippingMethod?.method === ShippingMethod.PickupPoint ? pickupPoint !== void 0 : true) && (consents && consentGiven ? consentGiven : false) && !!shippingMethod?.isValid;
  }
);
const isExpressCheckoutValid = createSelector(
  getSelectedShippingMethod,
  getSelectedStore,
  getSelectedPickupPoint,
  isConsentGiven,
  getShippingDetails,
  getConsents,
  (shippingMethod, store, pickupPoint, consentGiven, details, consents) => !!details && // some opcos don't have additional express checkout fields. In that case, it's valid by default.
  (isEmpty(details?.expressCheckout?.formData) || details.expressCheckout?.isValid) && (shippingMethod?.method === ShippingMethod.Pickup ? store !== void 0 : true) && (shippingMethod?.method === ShippingMethod.PickupPoint ? pickupPoint !== void 0 : true) && (consents && consentGiven ? consentGiven : false) && !!shippingMethod?.isValid
);
const isPrefillFinished = createSelector(
  getShipping,
  (shipping) => Boolean(shipping?.isPrefillFinished)
);
const getSelectedPostalCode = createSelector(
  getSelectedShippingMethod,
  getSelectedStore,
  getSelectedPickupPoint,
  getShippingData,
  (shippingMethod, selectedStore, selectedPickupPoint, shippingData) => {
    switch (shippingMethod?.method) {
      case "dpd-priority-delivery":
      case "home-delivery-express":
      case "home-delivery":
        return shippingData?.deliveryAddress?.postalCode ?? shippingData?.billingAddress?.postalCode;
      case "pick-up":
        return selectedStore?.postalCode;
      case "pick-up-point":
        return selectedPickupPoint?.postalCode;
      default:
        return shippingData?.deliveryAddress?.postalCode ?? shippingData?.billingAddress?.postalCode;
    }
  }
);
export {
  getBillingAddress,
  getBillingAddressFormData,
  getCompany,
  getCompanyFormData,
  getContactDetailsFormData,
  getDeliveryAddress,
  getDeliveryAddressFormData,
  getExpressCheckoutFormData,
  getInvoiceFormData,
  getInvoiceFormEnabledState,
  getSelectedPickupPoint,
  getSelectedPostalCode,
  getSelectedShippingMethod,
  getSelectedStore,
  getShippingData,
  getShippingDetails,
  hasStoredDeliveryAddress,
  isBillingAndShippingAddressSame,
  isDeliveryFormEnabled,
  isExpressCheckoutValid,
  isPrefillFinished,
  isShippingStepValid,
  shouldValidateMyAccount,
  shouldValidateServiceStore
};
//# sourceMappingURL=selectors.js.map
